import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Star, Plus, Loader2, Trash2 } from 'lucide-react';
import DashboardLayout from './components/layout/Dashboardlayout';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/Card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { useAuth } from './contexts/AuthContext';
import { supabase } from './integrations/supabase/client';
import { format } from 'date-fns';
import "./reviewspage.css"

const ReviewsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get('business');
  const { user, loading: authLoading } = useAuth();

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newReview, setNewReview] = useState({ customer_name: '', rating: 5, comment: '' });
  const keyss = process.env.REACT_API_KEY;

  useEffect(() => {
    if (!authLoading && !user) navigate('/auth');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (businessId) fetchReviews();
  }, [businessId]);

  const fetchReviews = async () => {
    //if (!businessId) return;
    setLoading(false);
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setReviews(data || []);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const createReview = async (reviewData) => {
    if (!businessId) return null;

    let sentiment = 'neutral';
    if (reviewData.comment && keyss) {
      try {
        const systemPrompt = 'You are a sentiment analysis AI. Return JSON like {"sentiment":"positive"}';
        const userPrompt = `Analyze sentiment for: "${reviewData.comment}"`;

        const response = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${keyss}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "google/gemini-2.5-flash",
            messages: [
              { role: "system", content: systemPrompt },
              { role: "user", content: userPrompt },
            ],
            response_format: { type: "json_object" },
          }),
        });

        const json = await response.json();
        const content = json.choices?.[0]?.message?.content || '{}';
        const sentimentData = JSON.parse(content);
        sentiment = sentimentData.sentiment || 'neutral';
      } catch (e) {
        console.error('Sentiment analysis failed:', e);
      }
    } else {
      if (reviewData.rating >= 4) sentiment = 'positive';
      else if (reviewData.rating <= 2) sentiment = 'negative';
    }

    try {
      const { data, error } = await supabase
        .from('reviews')
        .insert({
          business_id: businessId,
          customer_name: reviewData.customer_name,
          rating: reviewData.rating,
          comment: reviewData.comment || null,
          sentiment,
          source: reviewData.source || 'internal',
        })
        .select()
        .single();

      if (error) throw error;
      setReviews(prev => [data, ...prev]);
      return data;
    } catch (err) {
      console.error('Error creating review:', err);
      return null;
    }
  };

  const respondToReview = async (reviewId, responseText) => {
    try {
      const { error } = await supabase
        .from('reviews')
        .update({ responded: true, response_text: responseText })
        .eq('id', reviewId);

      if (error) throw error;

      setReviews(prev =>
        prev.map(r =>
          r.id === reviewId ? { ...r, responded: true, response_text: responseText } : r
        )
      );
    } catch (err) {
      console.error('Error responding to review:', err);
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId);
      if (error) throw error;

      setReviews(prev => prev.filter(r => r.id !== reviewId));
    } catch (err) {
      console.error('Error deleting review:', err);
    }
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    if (!newReview.customer_name || !newReview.comment) return;
    await createReview(newReview);
    setNewReview({ customer_name: '', rating: 5, comment: '' });
    setIsAddOpen(false);
  };

  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 style={{ width: '32px', height: '32px', animation: 'spin 1s linear infinite', color: '#6366f1' }} />
      </div>
    );
  }

  //if (!businessId) {
    //navigate('/dashboard');
    //return null;
  //}

  const safeReviews = Array.isArray(reviews) ? reviews : [];
  const avgRating = safeReviews.length > 0
    ? (safeReviews.reduce((s, r) => s + (r?.rating || 0), 0) / safeReviews.length).toFixed(1)
    : '0.0';

  const getSentimentStyle = (sentiment) => {
    if (sentiment === 'positive') return { bg: 'rgba(34, 197, 94, 0.1)', text: '#22c55e', border: 'rgba(34, 197, 94, 0.3)' };
    if (sentiment === 'negative') return { bg: 'rgba(239, 68, 68, 0.1)', text: '#ef4444', border: 'rgba(239, 68, 68, 0.3)' };
    return { bg: '#f3f4f6', text: '#9ca3af', border: '#e5e7eb' };
  };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', gap: '24px' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' },
    headerText: { display: 'flex', flexDirection: 'column', gap: '4px' },
    title: { fontSize: '30px', fontWeight: 'bold', margin: 0 },
    subtitle: { color: '#9ca3af', fontSize: '14px', margin: 0 },
    addButton: {
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '10px 20px', borderRadius: '8px', border: 'none',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#ffffff',
      fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s'
    },
    formContainer: { display: 'flex', flexDirection: 'column', gap: '16px' },
    input: { width: '100%', padding: '10px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', background: '#ffffff', outline: 'none', transition: 'border-color 0.2s' },
    ratingContainer: { display: 'flex', gap: '8px', justifyContent: 'center' },
    starButton: (isSelected) => ({ padding: '8px', borderRadius: '6px', background: 'transparent', border: 'none', cursor: 'pointer', color: isSelected ? '#f59e0b' : '#9ca3af', transition: 'all 0.2s' }),
    textarea: { width: '100%', minHeight: '80px', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', background: '#ffffff', fontFamily: 'inherit', resize: 'vertical', outline: 'none', transition: 'border-color 0.2s' },
    submitButton: { width: '100%', padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#6366f1', color: '#ffffff', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'background-color 0.2s' },
    reviewsGrid: { display: 'grid', gap: '16px' },
    reviewCard: (sentiment) => ({ borderColor: getSentimentStyle(sentiment).border }),
    reviewContent: { padding: '16px' },
    reviewHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '16px' },
    reviewInfo: { display: 'flex', flexDirection: 'column', gap: '4px' },
    customerName: { fontWeight: '600', fontSize: '16px', margin: 0 },
    starsContainer: { display: 'flex', gap: '2px' },
    sentimentBadge: (sentiment) => {
      const style = getSentimentStyle(sentiment);
      return { padding: '4px 8px', borderRadius: '9999px', fontSize: '12px', fontWeight: '500', textTransform: 'capitalize', background: style.bg, color: style.text, whiteSpace: 'nowrap' };
    },
    reviewComment: { color: '#9ca3af', fontSize: '14px', marginBottom: '8px', lineHeight: '1.5' },
    reviewDate: { fontSize: '12px', color: '#9ca3af' },
    emptyState: { textAlign: 'center', color: '#9ca3af', padding: '32px 0' },
    loader: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '32px 0' },
    deleteButton: { background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.2s' }
  };

  return (
    <DashboardLayout>
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerText}>
            <h1 style={styles.title}>Reviews</h1>
            <p style={styles.subtitle}>
              Average: ⭐ {avgRating} ({safeReviews.length} {safeReviews.length === 1 ? 'review' : 'reviews'})
            </p>
          </div>

          {/* Add Review Modal */}
          <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
            <DialogTrigger asChild>
              <button style={styles.addButton}>
                <Plus style={{ width: '20px', height: '20px' }} /> Add Review
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Review</DialogTitle></DialogHeader>
              <form onSubmit={handleAddReview} style={styles.formContainer}>
                <input
                  style={styles.input}
                  placeholder="Customer name"
                  value={newReview.customer_name || ''}
                  onChange={(e) => setNewReview(p => ({ ...p, customer_name: e.target.value }))}
                  required
                  onFocus={(e) => e.target.style.borderColor = '#6366f1'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
                <div style={styles.ratingContainer}>
                  {[1, 2, 3, 4, 5].map(r => {
                    const isSelected = (newReview.rating || 5) >= r;
                    return (
                      <button
                        key={r} type="button"
                        onClick={() => setNewReview(p => ({ ...p, rating: r }))}
                        style={styles.starButton(isSelected)}
                      >
                        <Star style={{ width: '24px', height: '24px' }} fill={isSelected ? 'currentColor' : 'none'} />
                      </button>
                    );
                  })}
                </div>
                <textarea
                  style={styles.textarea}
                  placeholder="Comment"
                  value={newReview.comment || ''}
                  onChange={(e) => setNewReview(p => ({ ...p, comment: e.target.value }))}
                  required
                  onFocus={(e) => e.target.style.borderColor = '#6366f1'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
                <button
                  type="submit"
                  style={styles.submitButton}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#5558e3'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#6366f1'}
                >
                  Add Review
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Reviews List */}
        <div style={styles.reviewsGrid}>
          {loading ? (
            <div style={styles.loader}>
              <Loader2 style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite', color: '#9ca3af' }} />
            </div>
          ) : safeReviews.length > 0 ? (
            safeReviews.map(review => (
              <ReviewCard
                key={review.id}
                review={review}
                respondToReview={respondToReview}
                deleteReview={deleteReview}
                styles={styles}
              />
            ))
          ) : (
            <p style={styles.emptyState}>No reviews yet. Add your first review to get started!</p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </DashboardLayout>
  );
};

// ReviewCard Component
const ReviewCard = ({ review, respondToReview, deleteReview, styles }) => {
  const [responseText, setResponseText] = useState(review.response_text || '');
  const [isResponding, setIsResponding] = useState(false);

  const handleResponseSubmit = async () => {
    if (!responseText.trim()) return;
    setIsResponding(true);
    await respondToReview(review.id, responseText);
    setIsResponding(false);
  };

  const sentiment = review.sentiment || 'neutral';

  return (
    <Card style={styles.reviewCard(sentiment)}>
      <CardContent style={styles.reviewContent}>
        <div style={styles.reviewHeader}>
          <div style={styles.reviewInfo}>
            <p style={styles.customerName}>{review.customer_name || 'Anonymous'}</p>
            <div style={styles.starsContainer}>
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  style={{ width: '16px', height: '16px', color: i < (review.rating || 0) ? '#f59e0b' : '#9ca3af' }}
                  fill={i < (review.rating || 0) ? '#f59e0b' : 'none'}
                />
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={styles.sentimentBadge(sentiment)}>{sentiment}</span>
            <button style={styles.deleteButton} onClick={() => deleteReview(review.id)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
              <Trash2 style={{ width: '16px', height: '16px', color: '#ef4444' }} />
            </button>
          </div>
        </div>

        {review.comment && <p style={styles.reviewComment}>{review.comment}</p>}
        <p style={styles.reviewDate}>{review.created_at ? format(new Date(review.created_at), 'MMM d, yyyy') : 'No date'}</p>

        {/* Respond to review */}
        {!review.responded && (
          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <textarea
              placeholder="Write your response..."
              style={styles.textarea}
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
            />
            <button
              style={styles.submitButton}
              onClick={handleResponseSubmit}
              disabled={isResponding}
            >
              {isResponding ? 'Responding...' : 'Submit Response'}
            </button>
          </div>
        )}

        {review.responded && review.response_text && (
          <div style={{ marginTop: '12px', fontSize: '14px', color: '#6b7280' }}>
            <strong>Your response:</strong> {review.response_text}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReviewsPage;
